"use client";

import * as React from "react";
import {
  Button,
  CloseButton,
  Drawer,
  Portal,
  VStack,
  HStack,
  Text,
  IconButton,
  Flex,
} from "@chakra-ui/react";
import { Checkbox } from "./checkbox";
import { FiTool } from "react-icons/fi";
import * as FaIcons from "react-icons/fa";
import { useNavigate } from "@tanstack/react-router";

export interface ToolOption {
  label: string;
  value: string;
  icon: string;
}

export interface ToolSelectDrawerProps {
  tools?: ToolOption[];
  onSelect: (selectedTools: string[]) => void;
}

export const ToolSelectDrawer = ({
  tools = [],
  onSelect,
}: ToolSelectDrawerProps) => {
  const [selectedTools, setSelectedTools] = React.useState<string[]>([]);
  const initialized = React.useRef(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!initialized.current && tools.length > 0) {
      setSelectedTools(tools.map((t) => t.value));
      initialized.current = true;
    }
  }, [tools]);

  const arraysEqual = (a: string[], b: string[]) =>
    a.length === b.length && a.every((val, idx) => val === b[idx]);

  const prevSelectedRef = React.useRef<string[]>([]);
  React.useEffect(() => {
    if (!arraysEqual(prevSelectedRef.current, selectedTools)) {
      onSelect(selectedTools);
      prevSelectedRef.current = selectedTools;
    }
  }, [selectedTools, onSelect]);

  const allToolsSelected =
    tools.length > 0 && tools.every((t) => selectedTools.includes(t.value));

  const toggleItem = (value: string) => {
    setSelectedTools((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const toggleAll = () => {
    setSelectedTools(allToolsSelected ? [] : tools.map((t) => t.value));
  };

  const toolSetting = () => {
    navigate({ to: "/tools" });
  };

  return (
    <Drawer.Root placement="bottom">
      <Drawer.Trigger asChild>
        <Button
          width="80px"
          variant="ghost"
          borderRadius="2xl"
          border="1px solid #e2e8f0"
          size="sm"
          px={3}
        >
          <Flex align="center" gap={1}>
            <FiTool />
            <Text>도구</Text>
          </Flex>
        </Button>
      </Drawer.Trigger>

      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content roundedTop="xl">
            <Drawer.Header>
              <Flex justify="space-between" align="center" w="100%" mt={8}>
                <Drawer.Title>도구 선택</Drawer.Title>
                <IconButton
                  aria-label="도구 설정"
                  variant="ghost"
                  size="sm"
                  onClick={toolSetting}
                >
                  <FiTool />
                </IconButton>
              </Flex>
            </Drawer.Header>

            <Drawer.Body>
              <VStack align="stretch" gap={2} mb={10}>
                <Button size="xs" variant="outline" onClick={toggleAll}>
                  {allToolsSelected ? "전체 해제" : "전체 선택"}
                </Button>
                {tools.map((tool) => (
                  <HStack
                    key={tool.value}
                    justify="space-between"
                    w="100%"
                    gap={2}
                  >
                    <HStack>
                      {FaIcons[tool.icon as keyof typeof FaIcons]
                        ? React.createElement(FaIcons[tool.icon as keyof typeof FaIcons])
                        : <FiTool />}
                      <Text>{tool.label}</Text>
                    </HStack>
                    <Checkbox
                      checked={selectedTools.includes(tool.value)}
                      onChange={() => toggleItem(tool.value)}
                    />
                  </HStack>
                ))}
              </VStack>
            </Drawer.Body>

            {/* <Drawer.Footer>
              <Drawer.ActionTrigger asChild>
                <Button variant="outline">취소</Button>
              </Drawer.ActionTrigger>
              <Button>확인</Button>
            </Drawer.Footer> */}

            <Drawer.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Drawer.CloseTrigger>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
};
